import { useQueryClient } from '@tanstack/react-query';
import { RotateCw } from 'lucide-react';
import React, { useEffect, useRef, useState, type RefObject } from 'react';

import { isPhoneDevice, isStandaloneApp } from '@/lib/device';

/** Pull distance (after resistance) that triggers a refresh, and the cap. */
export const PULL_THRESHOLD = 60;
const PULL_MAX = 90;
/** Finger travel counts half - the usual rubber-band feel. */
const RESISTANCE = 0.5;
/** Movement before deciding "pull" vs "scroll / swipe". */
const SLOP = 8;
/** Keep the spinner up at least this long, so a fast refetch still reads. */
const MIN_SPIN_MS = 600;

/** Where a pull must not start: typing, a dialog / modal, or an opt-out. */
const IGNORE = [
  'input',
  'textarea',
  'select',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[aria-modal="true"]',
  '[data-no-pull]',
].join(', ');

/**
 * Pull-to-refresh for the INSTALLED app on phones - a home-screen app has no
 * address bar or browser pull-to-refresh, and the app scrolls inside <main>
 * anyway. Pull down from the very top of the page to refetch what is on
 * screen: data only, no reload, so a half-filled form survives.
 *
 * Only listens on the page's scroll area (`target`), so the WhatsApp chat
 * and the call screen - outside it - never trigger it. Nor does a modal a
 * page renders inline (role="dialog", or any `position: fixed` layer), and a
 * pull that starts inside an inner panel that is itself scrolled down
 * scrolls that panel instead.
 *
 * The non-passive touchmove listener (needed to hold the page still while
 * pulling) is attached only for a gesture that may become a pull, so normal
 * scrolling never waits on it.
 */
export const PullToRefresh = ({
  target,
}: {
  target: RefObject<HTMLElement | null>;
}): React.JSX.Element | null => {
  const qc = useQueryClient();
  const enabled = isStandaloneApp() && isPhoneDevice();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);

  useEffect(() => {
    const el = target.current;
    if (!enabled || !el) return;

    let tracking = false;
    let decided = false;
    let startX = 0;
    let startY = 0;
    let dist = 0;

    /**
     * Not a pull of the page: some panel between the touch and the page is
     * scrolled down, or the touch is on a fixed layer (an inline modal).
     */
    const notThePage = (node: EventTarget | null): boolean => {
      let n = node instanceof Element ? node : null;
      while (n && n !== el) {
        if (n.scrollTop > 0) return true;
        if (window.getComputedStyle(n).position === 'fixed') return true;
        n = n.parentElement;
      }
      return false;
    };

    let moveAttached = false;
    const attachMove = (): void => {
      if (moveAttached) return;
      el.addEventListener('touchmove', onMove, { passive: false });
      moveAttached = true;
    };
    const detachMove = (): void => {
      if (!moveAttached) return;
      el.removeEventListener('touchmove', onMove);
      moveAttached = false;
    };

    const reset = (): void => {
      tracking = false;
      decided = false;
      dist = 0;
      detachMove();
      setPull(0);
    };

    const onStart = (e: TouchEvent): void => {
      const touch = e.touches[0];
      if (
        refreshingRef.current ||
        e.touches.length !== 1 ||
        !touch ||
        el.scrollTop > 0 ||
        (e.target instanceof Element && e.target.closest(IGNORE)) ||
        notThePage(e.target)
      ) {
        tracking = false;
        detachMove();
        return;
      }
      tracking = true;
      decided = false;
      dist = 0;
      startX = touch.clientX;
      startY = touch.clientY;
      attachMove();
    };

    const onMove = (e: TouchEvent): void => {
      const touch = e.touches[0];
      if (!tracking || !touch) return;
      if (e.touches.length !== 1) return reset(); // a pinch is not a pull
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      if (!decided) {
        // Sideways (a swipe / horizontal table scroll) or upward: not ours.
        if (Math.abs(dx) > SLOP && Math.abs(dx) >= Math.abs(dy)) return reset();
        if (dy < -SLOP) return reset();
        if (dy <= SLOP) return;
        decided = true;
      }
      if (el.scrollTop > 0) return reset();
      dist = Math.max(0, Math.min(PULL_MAX, (dy - SLOP) * RESISTANCE));
      setPull(dist);
      // Hold the page still while pulling (no iOS rubber band underneath).
      if (e.cancelable) e.preventDefault();
    };

    const onEnd = (): void => {
      detachMove();
      if (!tracking) return;
      const go = decided && dist >= PULL_THRESHOLD;
      tracking = false;
      decided = false;
      dist = 0;
      if (!go) {
        setPull(0);
        return;
      }
      refreshingRef.current = true;
      setRefreshing(true);
      setPull(PULL_THRESHOLD);
      void Promise.all([
        qc.refetchQueries({ type: 'active' }).catch(() => undefined),
        new Promise((resolve) => setTimeout(resolve, MIN_SPIN_MS)),
      ]).finally(() => {
        refreshingRef.current = false;
        setRefreshing(false);
        setPull(0);
      });
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      detachMove();
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, target, qc]);

  if (!enabled || (pull === 0 && !refreshing)) return null;
  const ready = refreshing || pull >= PULL_THRESHOLD;
  return (
    <div
      role="status"
      aria-label={refreshing ? 'Refreshing' : ready ? 'Release to refresh' : 'Pull to refresh'}
      // Position via `transform` only - Tailwind 4's translate-* utilities set
      // the separate `translate` property, which would add on top of this.
      className="pointer-events-none fixed top-[49px] left-1/2 z-40"
      style={{ transform: `translate(-50%, ${pull - 20}px)`, opacity: Math.min(1, pull / 30) }}
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--ds-border)] bg-[var(--bg)] text-[var(--fg-muted)] shadow-md">
        <RotateCw
          className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''} ${ready ? 'text-emerald-600' : ''}`}
          style={refreshing ? undefined : { transform: `rotate(${pull * 4}deg)` }}
          aria-hidden
        />
      </span>
    </div>
  );
};
